/**
 * Shared constants/types for the browser → server usage-analytics pipeline
 * (see docs/telemetry-events.md for the event registry and privacy rules).
 *
 * Kept dependency-free and free of any `window`/`process` access so it is safe
 * to import from the Node runtime (the forwarding route handler) and client
 * components alike — mirroring lib/logging/client-log-config.ts.
 */

/** Next.js route the browser posts batches to; forwards to the agent API. */
export const USAGE_EVENTS_ENDPOINT = "/api/usage-events";

/** Max events per client flush (the backend ingest accepts up to 200). */
export const MAX_USAGE_EVENT_BATCH = 20;

/** Max events the forwarding route will relay per POST (backend batch cap). */
export const MAX_USAGE_EVENT_FORWARD_BATCH = 200;

/** Cap on the JSON-serialized `props` object per event (backend drops > 2 KB). */
export const MAX_USAGE_EVENT_PROPS_CHARS = 2000;

/** Raw request-body cap for the forwarding route — flood guard, not a schema. */
export const MAX_USAGE_EVENTS_BODY_CHARS = 512 * 1024;

/** Event-name shape enforced by the backend ingest (`TelemetryController`). */
export const USAGE_EVENT_TYPE_PATTERN = /^[a-z0-9][a-z0-9._-]{0,63}$/;

/** One event in the `POST /api/telemetry/events` contract (org/user are server-stamped). */
export interface UsageEvent {
  eventType: string;
  surface: "ui";
  namespaceId: string | null;
  /** Opaque per-tab id — groups events into a browsing session, never a user id. */
  sessionId: string | null;
  clientVersion: string | null;
  /** Numbers, enums, and short ids only — never free text or user content. */
  props?: Record<string, unknown>;
  /** ISO-8601 timestamp from the client clock (advisory; server stamps its own). */
  occurredAt: string;
}

export interface UsageEventsPayload {
  events: UsageEvent[];
}
